/**
 * Servicio de API para comunicarse con el backend de QIA Licitator
 *
 * Este archivo contiene todas las funciones para hacer peticiones HTTP
 * al backend FastAPI usando axios.
 */

import axios from 'axios';
import { API_PREFIX } from '../../config/tenant';

// URL base del backend - Cambiar según el entorno
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// API_PREFIX añade '/met' cuando el frontend se sirve bajo /met (POC MET)
const API_V1 = `${API_BASE_URL}${API_PREFIX}/api/v1`;

/**
 * Cliente axios configurado con la URL base
 */
const apiClient = axios.create({
  baseURL: API_V1,
  timeout: 900000, // 15 minutos de timeout (análisis largos pueden tardar)
  headers: {
    'Content-Type': 'application/json',
  },
});

// ==========================================
// LICITACIONES - Análisis de documentos
// ==========================================

/**
 * Analiza documentos de licitación (respuesta inmediata con job_id)
 *
 * @param {File[]} files - Array de archivos PDF a analizar
 * @param {string} categoria - Categoría de la licitación (Consultoría, Ingeniería, BIM)
 * @param {string} comentarios - Instrucciones adicionales del usuario (opcional)
 * @returns {Promise<{job_id: string}>} - Promesa con el job_id para hacer polling
 */
export const analyzeLicitacion = async (files, categoria, comentarios = '') => {
  const formData = new FormData();

  // Añadir cada archivo al FormData
  files.forEach((file) => {
    formData.append('files', file);
  });

  // Añadir categoría
  formData.append('categoria', categoria);

  // Añadir comentarios adicionales si los hay
  if (comentarios && comentarios.trim()) {
    formData.append('comentarios_adicionales', comentarios.trim());
  }

  try {
    const response = await apiClient.post('/licitaciones/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30 segundos - ahora responde rápido con job_id
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Consulta el estado de un trabajo de análisis
 *
 * @param {string} jobId - ID del trabajo a consultar
 * @returns {Promise<{status: string, progress: number, message: string, result?: object}>}
 */
export const checkJobStatus = async (jobId) => {
  try {
    const response = await apiClient.get(`/licitaciones/status/${jobId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Descarga el archivo Excel con todos los análisis
 *
 * @returns {Promise<Blob>} - Promesa con el archivo Excel
 */
export const downloadExcel = async () => {
  try {
    const response = await apiClient.get('/licitaciones/download-excel', {
      responseType: 'blob', // Importante para descargar archivos
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Descarga el archivo PDF resumen de una licitación
 *
 * @param {string} fileName - Nombre del archivo PDF a descargar
 * @returns {Promise<Blob>} - Promesa con el archivo PDF
 */
export const downloadPdf = async (fileName) => {
  try {
    const response = await apiClient.get(`/licitaciones/download-pdf/${fileName}`, {
      responseType: 'blob', // Importante para descargar archivos
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Confirma y guarda los datos revisados por el usuario en el Excel
 * Se llama DESPUÉS de que el usuario haya revisado/editado los campos extraídos
 *
 * @param {Object[]} data - Array de diccionarios con los datos finales
 * @param {string} categoria - Categoría de la licitación (Consultoría, Ingeniería, BIM)
 * @returns {Promise} - Promesa con el resultado del guardado
 */
export const confirmAndSave = async (data, categoria) => {
  try {
    const response = await apiClient.post('/licitaciones/confirm-and-save', {
      data,
      categoria,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Obtiene el historial de análisis realizados
 *
 * @param {number} limit - Número de registros a obtener (default: 10)
 * @returns {Promise} - Promesa con el historial
 */
export const getHistory = async (limit = 10) => {
  try {
    const response = await apiClient.get(`/licitaciones/history?limit=${limit}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// ==========================================
// OFERTAS - Generación de borradores
// ==========================================

/**
 * Genera un borrador de oferta
 *
 * @param {Object} data - Datos para generar la oferta
 * @param {File[]} data.files - Archivos PDF de la licitación
 * @param {string} data.nombre_licitacion - Nombre/número de la licitación
 * @param {string} data.tipo_oferta - Tipo de oferta
 * @param {string} data.requisitos_especificos - Requisitos específicos (opcional)
 * @returns {Promise} - Promesa con el borrador generado
 */
export const generateOffer = async (data) => {
  const formData = new FormData();

  // Añadir archivos
  data.files.forEach((file) => {
    formData.append('files', file);
  });

  // Añadir datos del formulario
  formData.append('nombre_licitacion', data.nombre_licitacion);
  formData.append('tipo_oferta', data.tipo_oferta);

  if (data.requisitos_especificos) {
    formData.append('requisitos_especificos', data.requisitos_especificos);
  }

  try {
    const response = await apiClient.post('/ofertas/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Lista los documentos de referencia disponibles
 *
 * @returns {Promise} - Promesa con la lista de documentos
 */
export const getReferenceDocuments = async () => {
  try {
    const response = await apiClient.get('/ofertas/reference-documents');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// ==========================================
// PROPUESTAS - Generador de propuestas
// ==========================================

/**
 * Genera una propuesta a partir de los PDFs del pliego
 *
 * @param {File[]} files - Archivos PDF (PCAP, PPT, Anexos)
 * @returns {Promise<{success: boolean, message: string, job_id: string, status: string}>}
 */
export const generateProposal = async (files) => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  try {
    const response = await apiClient.post('/ofertas/generate-proposal', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Consulta el estado de un trabajo de generación de propuesta
 *
 * @param {string} jobId - ID del trabajo a consultar
 * @returns {Promise<{status: string, progress: number, message: string, result?: object}>}
 */
export const checkProposalStatus = async (jobId) => {
  try {
    const response = await apiClient.get(`/ofertas/proposal-status/${jobId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Descarga el archivo Word generado de la propuesta
 *
 * @param {string} docxFilename - Nombre del archivo .docx a descargar
 * @returns {Promise<Blob>} - Promesa con el archivo Word
 */
export const downloadProposalWord = async (docxFilename) => {
  try {
    const response = await apiClient.get(`/ofertas/download-proposal/${docxFilename}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// ==========================================
// GENERAL - Health check
// ==========================================

/**
 * Verifica que la API está funcionando
 *
 * @returns {Promise} - Promesa con el estado de la API
 */
export const healthCheck = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// ==========================================
// UTILIDADES
// ==========================================

/**
 * Maneja errores de la API y los formatea
 *
 * @param {Error} error - Error de axios
 * @returns {Error} - Error formateado
 */
const handleApiError = (error) => {
  if (error.response) {
    // El servidor respondió con un código de error
    const message = error.response.data?.detail || error.response.data?.error || 'Error del servidor';
    return new Error(message);
  } else if (error.request) {
    // La petición se hizo pero no hubo respuesta
    // Verificar si es un timeout
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new Error('El análisis está tardando más de lo esperado. El servidor sigue procesando en segundo plano. Por favor, espera unos minutos y recarga la página para ver los resultados.');
    }
    return new Error('No se pudo conectar con el servidor. Verifica que el backend está corriendo y accesible.');
  } else {
    // Algo sucedió al configurar la petición
    return new Error(error.message);
  }
};

/**
 * Descarga un archivo desde un Blob
 *
 * @param {Blob} blob - Blob del archivo
 * @param {string} filename - Nombre del archivo
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Descarga texto como archivo
 *
 * @param {string} text - Contenido del archivo
 * @param {string} filename - Nombre del archivo
 * @param {string} mimeType - Tipo MIME del archivo
 */
export const downloadTextFile = (text, filename, mimeType = 'text/plain') => {
  const blob = new Blob([text], { type: mimeType });
  downloadFile(blob, filename);
};

const api = {
  analyzeLicitacion,
  checkJobStatus,
  confirmAndSave,
  downloadExcel,
  downloadPdf,
  getHistory,
  generateOffer,
  getReferenceDocuments,
  generateProposal,
  checkProposalStatus,
  downloadProposalWord,
  healthCheck,
  downloadFile,
  downloadTextFile,
};

export default api;
